import { useState, useCallback } from 'react';
import { products } from '@/mocks/products';

export interface CartItem {
  productId: string;
  quantity: number;
}

const STORAGE_KEY = 'kenstore_cart';

function readCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(cart: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
}

export function useCart() {
  const [cart, setCartState] = useState<CartItem[]>(readCart);

  const setCart = useCallback((next: CartItem[]) => {
    writeCart(next);
    setCartState(next);
  }, []);

  const addToCart = useCallback((productId: string, quantity = 1) => {
    const current = readCart();
    const existing = current.find((i) => i.productId === productId);
    let next: CartItem[];
    if (existing) {
      next = current.map((i) =>
        i.productId === productId ? { ...i, quantity: i.quantity + quantity } : i,
      );
    } else {
      next = [...current, { productId, quantity }];
    }
    writeCart(next);
    setCartState(next);
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    const current = readCart();
    if (quantity <= 0) {
      const next = current.filter((i) => i.productId !== productId);
      writeCart(next);
      setCartState(next);
      return;
    }
    const next = current.map((i) =>
      i.productId === productId ? { ...i, quantity } : i,
    );
    writeCart(next);
    setCartState(next);
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    const current = readCart();
    const next = current.filter((i) => i.productId !== productId);
    writeCart(next);
    setCartState(next);
  }, []);

  const clearCart = useCallback(() => {
    writeCart([]);
    setCartState([]);
  }, []);

  const getCartCount = useCallback(() => {
    return cart.reduce((sum, i) => sum + i.quantity, 0);
  }, [cart]);

  const getCartTotal = useCallback(() => {
    return cart.reduce((sum, item) => {
      const product = products.find((p) => p.id === item.productId);
      if (!product) return sum;
      const price = product.salePrice ?? product.price;
      return sum + price * item.quantity;
    }, 0);
  }, [cart]);

  const getDeliveryFee = useCallback(() => {
    const total = getCartTotal();
    return total >= 5000 ? 0 : 300;
  }, [getCartTotal]);

  return {
    cart,
    setCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartCount,
    getCartTotal,
    getDeliveryFee,
  };
}